import { ClearOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Select } from "antd"
import { useRef } from "react"

import Flex from "#blocks/Flex"
import SearchBar, { type SearchBarRef } from "#blocks/SearchBar"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES } from "#customTypes/composedTypes/authTypes"
import { SETTINGS_KEYS, UserSettings } from "#customTypes/globalTypes"
import { SemesterOptions } from "#forms/SemesterDropdown"
import { useResourcePermissions } from "#hooks/newHooks/useHasPermission"
import { getCurrentSemester } from "#utils/semesterHelpers"

const SETTINGS_KEY_TO_RESOURCES = {
  [SETTINGS_KEYS.CLASSES]: RESOURCES.CLASS,
  [SETTINGS_KEYS.STUDENTS]: RESOURCES.STUDENT,
  [SETTINGS_KEYS.TRYOUTS]: RESOURCES.TRYOUT,
  [SETTINGS_KEYS.MAKEUPS]: RESOURCES.MAKEUP,
  [SETTINGS_KEYS.CURRICULUMS]: RESOURCES.CURRICULUM_PLAN,
  [SETTINGS_KEYS.PROJECTS]: RESOURCES.PROJECT,
  [SETTINGS_KEYS.USERS]: RESOURCES.USER,
  [SETTINGS_KEYS.EVALUATIONS]: RESOURCES.EVALUATION,
}

const TableFiltersRow = ({
  onAdd,
  onClear,
  onSearch,
  searchPlaceholder = "Search",
  settingsKey,
}: {
  onAdd?: () => void
  onClear?: () => Partial<UserSettings> | void
  onSearch: (search: string) => void
  searchPlaceholder?: string
  settingsKey: SETTINGS_KEYS
}) => {
  const searchBarRef = useRef<SearchBarRef>(null)

  const { canCreate } = useResourcePermissions(
    SETTINGS_KEY_TO_RESOURCES[settingsKey]
  )

  const { getUserSetting, setUserSetting, setBulkUserSettings } =
    useUserContext()

  const searchKey = `${settingsKey}TableSearchTerm` as keyof UserSettings
  const filterSemesterKey = `${settingsKey}TableSemester` as keyof UserSettings
  const resultsCountKey =
    `${settingsKey}TableResultsCount` as keyof UserSettings

  const defaultSearchTerm = getUserSetting<string>(searchKey)
  const resultsCount = getUserSetting<number>(resultsCountKey)
  const filterSemester = getUserSetting<string>(filterSemesterKey)

  const handleSearch = (searchTerm: string) => {
    setUserSetting(searchKey, searchTerm)
    onSearch?.(searchTerm)
  }

  const handleSemesterChange = (semester: string) => {
    setUserSetting(filterSemesterKey, semester)
  }

  const handleResultsCountChange = (resultsCount: number) => {
    setUserSetting(resultsCountKey, resultsCount)
  }

  const handleClear = () => {
    const settings: Partial<UserSettings> = {
      [searchKey]: "",
      [resultsCountKey]: 20,
    }

    if (settingsKey !== "students" && settingsKey !== "users") {
      // @ts-expect-error - This is a valid assignment
      settings[filterSemesterKey] = getCurrentSemester()
    }

    if (settingsKey === "users") {
      settings.usersTableShowDisabledUsers = false
    }

    const additionalSettings = onClear?.()
    if (additionalSettings) {
      Object.assign(settings, additionalSettings)
    }

    setBulkUserSettings(settings)
    onSearch("")
    searchBarRef.current?.clear()
  }

  return (
    <Flex gap={24} style={{ alignItems: "center" }}>
      {onAdd && canCreate && (
        <Button onClick={onAdd} type="primary">
          <PlusOutlined /> New
        </Button>
      )}

      <Flex gap={12}>
        {settingsKey !== "students" && settingsKey !== "users" && (
          <Select
            data-testid="table-semester-filter"
            placeholder="Select semester"
            value={filterSemester}
            onChange={handleSemesterChange}
            style={{ width: 170 }}
          >
            {SemesterOptions({
              includeAll: true,
            })}
          </Select>
        )}

        <SearchBar
          ref={searchBarRef}
          onSearch={handleSearch}
          placeholder={searchPlaceholder}
          defaultSearchTerm={defaultSearchTerm}
        />

        <Select
          labelRender={({ value }) => `Max ${value} results`}
          placeholder="Results count"
          value={resultsCount}
          onChange={handleResultsCountChange}
        >
          <Select.Option value={10}>10</Select.Option>
          <Select.Option value={20}>20</Select.Option>
          <Select.Option value={50}>50</Select.Option>
          <Select.Option value={100}>100</Select.Option>
        </Select>

        <Button
          onClick={handleClear}
          icon={<ClearOutlined />}
          style={{ marginLeft: 20 }}
        >
          Clear filters
        </Button>
      </Flex>
    </Flex>
  )
}

export default TableFiltersRow
